/**
 * AudienceEstimate
 * Displays audience size / keyword volume estimates
 * Adapts display based on platform and campaign type
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Users, TrendingUp, RefreshCw, AlertCircle, Search } from 'lucide-react';
import { debounce } from 'lodash';

export default function AudienceEstimate({
    platform,
    brandId,
    userId,
    targeting,
    campaignType = null, // For Google: 'SEARCH', 'DISPLAY', 'VIDEO'
    onEstimateChange,
    autoRefresh = true,
    refreshDelay = 1500
}) {
    const [estimate, setEstimate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch estimate with debounce
    const fetchEstimate = useCallback(
        debounce(async (targetingData) => {
            if (!targetingData || !hasRequiredTargeting(targetingData)) {
                setEstimate(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`/api/targeting/${platform}/estimate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        brandId,
                        userId,
                        targeting: targetingData,
                        campaignType
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to get estimate');
                }

                const data = await response.json();
                setEstimate(data.estimate);
                onEstimateChange?.(data.estimate);
            } catch (err) {
                console.error('Estimate error:', err);
                setError('Unable to estimate audience');
                setEstimate(null);
            } finally {
                setLoading(false);
            }
        }, refreshDelay),
        [platform, brandId, userId, campaignType, onEstimateChange, refreshDelay]
    );

    // Auto-refresh on targeting change
    useEffect(() => {
        if (autoRefresh) {
            fetchEstimate(targeting);
        }
    }, [targeting, autoRefresh, fetchEstimate]);

    // Check if we have enough targeting to estimate
    const hasRequiredTargeting = (t) => {
        if (!t) return false;

        // Need at least location
        const hasLocation = t.locations?.included?.length > 0;

        return hasLocation;
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
        return num.toLocaleString();
    };

    const getAudienceSizeDescription = (size) => {
        if (!size) return '';
        if (size < 1000) return 'Very narrow audience';
        if (size < 10000) return 'Narrow audience';
        if (size < 100000) return 'Small audience';
        if (size < 1000000) return 'Medium audience';
        if (size < 10000000) return 'Large audience';
        return 'Very large audience';
    };

    const getHealthIndicator = (size) => {
        if (!size) return 'neutral';
        if (platform === 'linkedin' && size < 300) return 'critical';
        if (size < 1000) return 'warning';
        if (size < 10000) return 'caution';
        return 'good';
    };

    // Google Search campaigns show keyword volume instead
    const isKeywordVolumeMode = platform === 'google' && campaignType === 'SEARCH';

    return (
        <div className="audience-estimate">
            <div className="estimate-header">
                <div className="header-icon">
                    {isKeywordVolumeMode ? <Search size={16} /> : <Users size={16} />}
                </div>
                <span className="header-title">
                    {isKeywordVolumeMode ? 'Keyword Volume' : 'Audience Estimate'}
                </span>
                <button
                    type="button"
                    className="refresh-btn"
                    onClick={() => fetchEstimate(targeting)}
                    disabled={loading}
                >
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            <div className="estimate-content">
                {loading && (
                    <div className="loading-state">
                        <RefreshCw size={20} className="spinning" />
                        <span>Calculating...</span>
                    </div>
                )}

                {error && !loading && (
                    <div className="error-state">
                        <AlertCircle size={16} />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && !error && !estimate && (
                    <div className="empty-state">
                        <span>Add targeting criteria to see estimates</span>
                    </div>
                )}

                {!loading && !error && estimate && (
                    <>
                        {isKeywordVolumeMode ? (
                            // Keyword volume display for Google Search
                            <div className="keyword-volume">
                                <div className="volume-main">
                                    <span className="volume-number">
                                        {formatNumber(estimate.totalMonthlySearches)}
                                    </span>
                                    <span className="volume-label">monthly searches</span>
                                </div>

                                {estimate.keywords && estimate.keywords.length > 0 && (
                                    <div className="keyword-breakdown">
                                        <span className="breakdown-title">Top keywords by volume</span>
                                        {estimate.keywords.slice(0, 5).map((kw, idx) => (
                                            <div key={idx} className="keyword-row">
                                                <span className="kw-text">{kw.keyword}</span>
                                                <span className="kw-volume">{formatNumber(kw.avgMonthlySearches)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            // Audience size display for other platforms
                            <div className="audience-size">
                                <div className={`size-main ${getHealthIndicator(estimate.users)}`}>
                                    <div className="size-range">
                                        {estimate.usersLowerBound !== estimate.usersUpperBound ? (
                                            <>
                                                <span className="size-number">
                                                    {formatNumber(estimate.usersLowerBound)}
                                                </span>
                                                <span className="size-separator">-</span>
                                                <span className="size-number">
                                                    {formatNumber(estimate.usersUpperBound)}
                                                </span>
                                            </>
                                        ) : (
                                            <span className="size-number">
                                                {formatNumber(estimate.users)}
                                            </span>
                                        )}
                                    </div>
                                    <span className="size-label">
                                        {platform === 'linkedin' ? 'members' : 'people'}
                                    </span>
                                </div>

                                <div className="size-description">
                                    {getAudienceSizeDescription(estimate.users)}
                                </div>

                                {/* Platform-specific warnings */}
                                {platform === 'linkedin' && estimate.users < 300 && (
                                    <div className="size-warning critical">
                                        LinkedIn requires minimum 300 members
                                    </div>
                                )}

                                {estimate.users > 0 && estimate.users < 1000 && (
                                    <div className="size-warning">
                                        Audience may be too small for effective delivery
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Daily reach estimate if available */}
                        {estimate.dailyOutcomesLowerBound && (
                            <div className="daily-reach">
                                <TrendingUp size={14} />
                                <span>
                                    Est. daily reach: {formatNumber(estimate.dailyOutcomesLowerBound)} - {formatNumber(estimate.dailyOutcomesUpperBound)}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>

            <style>{`
                .audience-estimate {
                    background: var(--color-surface);
                    border: 1px solid var(--color-border);
                    border-radius: 8px;
                    overflow: hidden;
                }

                .estimate-header {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1rem;
                    background: var(--color-bg-primary);
                    border-bottom: 1px solid var(--color-border);
                }

                .header-icon {
                    color: var(--color-text-tertiary);
                }

                .header-title {
                    flex: 1;
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: var(--color-text-primary);
                }

                .refresh-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 0.25rem;
                    background: transparent;
                    border: none;
                    color: var(--color-text-tertiary);
                    cursor: pointer;
                    transition: color 0.15s;
                }

                .refresh-btn:hover {
                    color: var(--color-text-primary);
                }

                .refresh-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .estimate-content {
                    padding: 1rem;
                }

                .loading-state,
                .error-state,
                .empty-state {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1rem;
                    color: var(--color-text-tertiary);
                    font-size: 0.875rem;
                }

                .error-state {
                    color: var(--color-error);
                }

                .audience-size,
                .keyword-volume {
                    text-align: center;
                }

                .size-main {
                    margin-bottom: 0.5rem;
                }

                .size-main.good { color: var(--color-success); }
                .size-main.caution { color: var(--color-warning); }
                .size-main.warning { color: var(--color-warning); }
                .size-main.critical { color: var(--color-error); }
                .size-main.neutral { color: var(--color-text-primary); }

                .size-range {
                    display: flex;
                    align-items: baseline;
                    justify-content: center;
                    gap: 0.25rem;
                }

                .size-number {
                    font-size: 1.75rem;
                    font-weight: 600;
                }

                .size-separator {
                    font-size: 1.25rem;
                    opacity: 0.5;
                }

                .size-label {
                    display: block;
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                    margin-top: 0.125rem;
                }

                .size-description {
                    font-size: 0.8125rem;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.5rem;
                }

                .size-warning {
                    display: inline-block;
                    padding: 0.25rem 0.5rem;
                    background: color-mix(in srgb, var(--color-warning) 15%, transparent);
                    color: var(--color-warning);
                    border-radius: 4px;
                    font-size: 0.75rem;
                    margin-top: 0.5rem;
                }

                .size-warning.critical {
                    background: color-mix(in srgb, var(--color-error) 15%, transparent);
                    color: var(--color-error);
                }

                .volume-main {
                    margin-bottom: 1rem;
                }

                .volume-number {
                    display: block;
                    font-size: 1.75rem;
                    font-weight: 600;
                    color: var(--color-text-primary);
                }

                .volume-label {
                    display: block;
                    font-size: 0.75rem;
                    color: var(--color-text-tertiary);
                }

                .keyword-breakdown {
                    text-align: left;
                    padding-top: 0.75rem;
                    border-top: 1px solid var(--color-border);
                }

                .breakdown-title {
                    display: block;
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--color-text-tertiary);
                    margin-bottom: 0.5rem;
                }

                .keyword-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 0.25rem 0;
                    font-size: 0.8125rem;
                }

                .kw-text {
                    color: var(--color-text-primary);
                }

                .kw-volume {
                    color: var(--color-text-tertiary);
                }

                .daily-reach {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.375rem;
                    margin-top: 0.75rem;
                    padding-top: 0.75rem;
                    border-top: 1px solid var(--color-border);
                    font-size: 0.8125rem;
                    color: var(--color-text-tertiary);
                }
            `}</style>
        </div>
    );
}
